import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useSWRConfig } from 'swr';
import { useSignaturesList, cloneSignature, SIGNATURES_MANAGE_KEY, SIGNATURES_LIST_KEY } from './useData';
import { useAuth } from '../../contexts/AuthContext';
import { moduleConfig, getAssignedUsersLabel, getAssignedAccountsLabel, canUserEditSignature } from './config';
import { useTheme } from '../../contexts/ThemeContext';
import { cn } from '../../lib/utils';
import ConfirmationDialog from '../../components/ui/ConfirmationDialog';
import { useNotification } from '../../contexts/NotificationContext';
import { useModuleConfigStrings } from '../../i18n/useModuleConfigStrings';

const List: React.FC = () => {
  const { colors } = useTheme();
  const { showError } = useNotification();
  const strings = useModuleConfigStrings(moduleConfig.module.i18nKey, moduleConfig);
  const isMobile = false;
  const { user: currentUser } = useAuth();
  const { mutate: globalMutate } = useSWRConfig();
  const { data: signatures, isLoading, mutate, canManage } = useSignaturesList();
  const [deleteConfirmId, setDeleteConfirmId] = useState<number | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [cloningId, setCloningId] = useState<number | null>(null);

  const handleDeleteClick = (id: number) => setDeleteConfirmId(id);

  const handleClone = async (id: number) => {
    setCloningId(id);
    try {
      await cloneSignature(id);
      await Promise.all([
        mutate(),
        globalMutate(SIGNATURES_MANAGE_KEY),
        globalMutate(SIGNATURES_LIST_KEY),
        globalMutate('/emails/signatures'),
      ]);
    } catch {
      showError('Failed to clone signature.');
    } finally {
      setCloningId(null);
    }
  };

  const handleDeleteConfirm = async () => {
    const id = deleteConfirmId;
    if (id == null) return;
    setDeleteConfirmId(null);
    setDeletingId(id);
    try {
      await moduleConfig.delete.handler(id);
      await Promise.all([
        mutate(),
        globalMutate(SIGNATURES_MANAGE_KEY),
        globalMutate(SIGNATURES_LIST_KEY),
        globalMutate('/emails/signatures'),
      ]);
    } catch {
      showError(strings.deleteErrorMessage);
    } finally {
      setDeletingId(null);
    }
  };

  if (isLoading) {
    return (
      <div className={cn('p-6', colors.textMuted)}>
        Loading signatures…
      </div>
    );
  }

  return (
    <div className={cn('w-full max-w-[90rem] mx-auto', isMobile ? 'px-4 py-4' : 'px-4 py-6 sm:px-6')}>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-end gap-4 mb-6">
        {canManage && (
          <Link
            to={moduleConfig.module.route.new}
            className={cn(
              'inline-flex items-center justify-center gap-2 rounded-lg text-sm font-medium px-4 py-2.5 h-10 shadow-sm border border-transparent',
              colors.primary,
              colors.primaryText,
              'hover:opacity-90 transition-opacity'
            )}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            New signature
          </Link>
        )}
      </div>

      {signatures.length === 0 ? (
        <div
          className={cn(
            'rounded-xl border-2 border-dashed py-12 sm:py-16 px-4 sm:px-6 text-center',
            colors.border,
            colors.surfaceHover
          )}
        >
          <div className="mx-auto w-12 h-12 rounded-full flex items-center justify-center bg-gray-100 dark:bg-gray-800 mb-4">
            <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
            </svg>
          </div>
          <p className={cn('text-base', colors.text)}>{strings.emptyNoData}</p>
        </div>
      ) : (
        <ul className="w-full space-y-4">
          {signatures.map((sig) => {
            const canEdit = canUserEditSignature(sig, currentUser?.id);
            return (
            <li
              key={sig.id}
              className={cn(
                'min-w-0 w-full sm:min-w-[32rem] rounded-xl border overflow-hidden transition-all',
                colors.border,
                colors.surface,
                'hover:shadow-md'
              )}
            >
              <div className="flex flex-col sm:flex-row sm:items-start gap-4 p-4 sm:p-5">
                <Link
                  to={moduleConfig.module.route.view(sig.id)}
                  className="flex items-start gap-4 min-w-0 flex-1 group"
                >
                  <div
                    className={cn(
                      'flex-shrink-0 w-11 h-11 rounded-xl flex items-center justify-center transition-colors',
                      canEdit
                        ? 'bg-indigo-100 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 group-hover:bg-indigo-200 dark:group-hover:bg-indigo-800/40'
                        : 'bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400 group-hover:bg-gray-200 dark:group-hover:bg-gray-700/50',
                    )}
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                    </svg>
                  </div>
                  <div className="min-w-0 flex-1 overflow-hidden">
                    <h2 className={cn('font-semibold text-base group-hover:underline break-words', colors.text)}>{sig.name}</h2>
                    <div className="flex flex-wrap items-center gap-x-2 gap-y-1 mt-1">
                      {sig.isDefault && (
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-indigo-100 text-indigo-800 dark:bg-indigo-900/40 dark:text-indigo-300">
                          Default
                        </span>
                      )}
                      {canManage && (
                        <>
                          <span className={cn('text-sm break-words', colors.textMuted)}>
                            {getAssignedUsersLabel(sig)}
                          </span>
                          <span className={cn('text-sm break-words', colors.textMuted)}>·</span>
                        </>
                      )}
                      <span className={cn('text-sm break-words', colors.textMuted)}>
                        {getAssignedAccountsLabel(sig)}
                      </span>
                    </div>
                    {(sig.isViewOnly || !canEdit) && (
                      <p className={cn('text-sm mt-1', colors.textMuted)}>
                        Shared with multiple users — view only.
                      </p>
                    )}
                    {sig.body && (
                      <p className={cn('text-sm mt-2 line-clamp-2 break-words', colors.textSecondary)}>
                        {sig.body.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim().slice(0, 120)}
                        {sig.body.length > 120 ? '…' : ''}
                      </p>
                    )}
                  </div>
                </Link>
                <div className={cn('flex items-center gap-1 flex-shrink-0 sm:pl-2 border-t sm:border-t-0 sm:border-l pt-4 sm:pt-0 sm:pl-4', colors.border)}>
                  {canManage && (
                    <button
                      type="button"
                      onClick={() => handleClone(sig.id)}
                      disabled={cloningId === sig.id}
                      className={cn(
                        'p-2.5 rounded-lg transition-colors',
                        'text-gray-600 hover:text-gray-800 hover:bg-gray-100 dark:text-gray-400 dark:hover:text-gray-200 dark:hover:bg-gray-700/50',
                        cloningId === sig.id && 'opacity-50 cursor-not-allowed'
                      )}
                      title="Clone signature"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h2v2h2a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                      </svg>
                    </button>
                  )}
                  <Link
                    to={moduleConfig.module.route.view(sig.id)}
                    className="p-2.5 text-gray-600 hover:text-gray-800 hover:bg-gray-100 dark:text-gray-400 dark:hover:text-gray-200 dark:hover:bg-gray-700/50 rounded-lg transition-colors"
                    title="View"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                  </Link>
                  {canEdit && (
                  <Link
                    to={moduleConfig.module.route.edit(sig.id)}
                    className="p-2.5 text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 rounded-lg transition-colors"
                    title="Edit"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                  </Link>
                  )}
                  {canManage && (
                    <button
                      type="button"
                      onClick={() => handleDeleteClick(sig.id)}
                      disabled={deletingId === sig.id}
                      className={cn(
                        'p-2.5 text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors',
                        deletingId === sig.id && 'opacity-50 cursor-not-allowed'
                      )}
                      title="Delete"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  )}
                </div>
              </div>
            </li>
            );
          })}
        </ul>
      )}

      <ConfirmationDialog
        isOpen={deleteConfirmId != null}
        onClose={() => setDeleteConfirmId(null)}
        onConfirm={handleDeleteConfirm}
        title={strings.deleteTitle}
        message={strings.deleteMessage}
        confirmText={strings.deleteConfirmText}
        cancelText={strings.deleteCancelText}
        confirmVariant="danger"
      />
    </div>
  );
};

export default List;
